import { ClientUserRow } from "@/data/models/ClientAdministrationRow";
import { Role } from "@/data/models/Role";
import { matchesGroupedSelection, orderedSelection } from "@/utils/GroupedFilterSelection";

/**
 * The admin portal's user list: its grouped multi-filter (the shared rule in
 * `GroupedFilterSelection.ts`), search and sort. Pure, so the rules are pinned by tests.
 *
 * Deleted users are hidden by the default selection (Aktiv and Gesperrt ticked), as IRES Admin
 * hides them; ticking Gelöscht shows them (Taimoor, 2026-10-02).
 */
export enum UserListFilterGroup {
  STATUS = "STATUS",
  ROLE = "ROLE",
}

export enum UserStatusOption {
  /** Neither blocked nor deleted. */
  ACTIVE = "ACTIVE",
  /** Blocked and not deleted. */
  BLOCKED = "BLOCKED",
  DELETED = "DELETED",
}

/** One tickable option: a status, or a role (`Role` values, distinct from the statuses). */
export type UserListOption = UserStatusOption | Role;

/** Every option in menu order, with its group: the statuses, then the roles in `Role` order. */
export const USER_LIST_OPTIONS: Array<{ option: UserListOption; group: UserListFilterGroup }> = [
  ...Object.values(UserStatusOption).map((option) => ({ option, group: UserListFilterGroup.STATUS })),
  ...Object.values(Role).map((option) => ({ option, group: UserListFilterGroup.ROLE })),
];

/** What the list opens with: every user who is not deleted. */
export const DEFAULT_USER_LIST_SELECTION: UserListOption[] = [UserStatusOption.ACTIVE, UserStatusOption.BLOCKED];

export function userListOptionGroup(option: UserListOption): UserListFilterGroup | null {
  return USER_LIST_OPTIONS.find((entry) => entry.option === option)?.group ?? null;
}

/** The user's status: deleted wins over blocked, as a deleted user cannot sign in either way. */
export function userStatus(row: Pick<ClientUserRow, "deleted" | "blocked">): UserStatusOption {
  if (row.deleted) {
    return UserStatusOption.DELETED;
  }
  return row.blocked ? UserStatusOption.BLOCKED : UserStatusOption.ACTIVE;
}

function matchesOption(row: ClientUserRow, option: UserListOption): boolean {
  switch (option) {
    case UserStatusOption.ACTIVE:
    case UserStatusOption.BLOCKED:
    case UserStatusOption.DELETED:
      return userStatus(row) === option;
    default:
      return row.role === option;
  }
}

/** Whether a user passes the ticked options: any within a group, all groups with a tick. */
export function matchesUserListSelection(row: ClientUserRow, selection: UserListOption[]): boolean {
  return matchesGroupedSelection(row, selection, userListOptionGroup, matchesOption);
}

/** Whether the selection is the default one, so the header need not name it. */
export function isDefaultUserListSelection(selection: UserListOption[]): boolean {
  return (
    selection.length === DEFAULT_USER_LIST_SELECTION.length &&
    DEFAULT_USER_LIST_SELECTION.every((option) => selection.includes(option))
  );
}

/** A case-insensitive substring of the nickname, first or last name; blank matches everything. */
export function matchesUserSearch(row: ClientUserRow, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();

  if (needle === "") {
    return true;
  }

  return [row.nickname, row.firstName, row.lastName].some(
    (value) => value !== null && value.toLocaleLowerCase().includes(needle)
  );
}

/** IRES Office's own user list sorts. */
export enum UserListSort {
  NICKNAME = "NICKNAME",
  FIRST_NAME = "FIRST_NAME",
  LAST_NAME = "LAST_NAME",
}

export const USER_LIST_SORTS: UserListSort[] = Object.values(UserListSort);

function sortValue(row: ClientUserRow, sort: UserListSort): string | null {
  switch (sort) {
    case UserListSort.FIRST_NAME:
      return row.firstName;
    case UserListSort.LAST_NAME:
      return row.lastName;
    default:
      return row.nickname;
  }
}

/**
 * Filter, search and sort; the input is not modified. Alphabetical in German order, ignoring case;
 * users without the sorted value last; ties by nickname, then id, so the order is stable.
 */
export function selectUserListRows(
  rows: ClientUserRow[],
  selection: UserListOption[],
  query: string,
  sort: UserListSort
): ClientUserRow[] {
  const compare = (a: string | null, b: string | null): number => {
    if (a === null || b === null) {
      return Number(a === null) - Number(b === null);
    }
    return a.localeCompare(b, "de", { sensitivity: "base", numeric: true });
  };

  return rows
    .filter((row) => matchesUserListSelection(row, selection) && matchesUserSearch(row, query))
    .sort(
      (a, b) =>
        compare(sortValue(a, sort), sortValue(b, sort)) ||
        compare(a.nickname, b.nickname) ||
        a.id.localeCompare(b.id)
    );
}

/** The ticked options in menu order, for the header ("Benutzer: 12 (Gelöscht)"). */
export function orderedUserListSelection(selection: UserListOption[]): UserListOption[] {
  return orderedSelection(selection, USER_LIST_OPTIONS.map((entry) => entry.option));
}
